import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Content from '../../components/Content';
import Loader from '../../components/Loader';
import Error from '../../components/Error';
import styles from './IDSRules.module.css';
import Modal from '../../components/Modal';
import ModalHeading from '../../components/Modal/ModalHeading';
import FileDropZone from '../../components/FileDropZone';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';

const FRONTEND_IMPORT_IDS_ERROR_CODES = {
  INITIAL_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
  SINGLE_RULE_ADD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
  FILE_IMPORT_FAILED: ErrorCodes.FILE_PROCESSING_FAILED,
};

const buildImportIDSSupportError = (errorCode, pageInfo, sourceMessage) =>
  formatFrontendSupportError({
    errorCode,
    pageInfo,
    sourceMessage,
  });

const ImportIDSScreen = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState({ single: false, file: false });
  const [showModal, setShowModal] = useState(false);
  const [availableTargets, setAvailableTargets] = useState([]);
  const [targetCompany, setTargetCompany] = useState('');
  const [ruleEnabled, setRuleEnabled] = useState(true);
  const [importStats, setImportStats] = useState(null);
  // Upload feedback: 'uploading' shows a % bar during transfer, then 'processing'
  // (indeterminate) while the server parses/imports a big ruleset.
  const [uploadPhase, setUploadPhase] = useState('idle');   // idle | uploading | processing
  const [uploadProgress, setUploadProgress] = useState(0);
  const [supportErrorMessage, setSupportErrorMessage] = useState('');
  const [copiedSupportError, setCopiedSupportError] = useState(false);
  
  // Single rule state
  const [singleRule, setSingleRule] = useState({ value: '', error: '' });
  
  // File upload state
  const [ruleFile, setRuleFile] = useState({ value: null, error: '' });

  const setImportIDSSupportError = React.useCallback((errorCode, pageInfo, sourceMessage, fatal = false) => {
    setSupportErrorMessage(buildImportIDSSupportError(errorCode, pageInfo, sourceMessage));
    setCopiedSupportError(false);
    if (fatal) {
      setError(true);
    }
  }, []);

  const clearImportIDSSupportError = React.useCallback(() => {
    setSupportErrorMessage('');
    setCopiedSupportError(false);
  }, []);

  const handleCopySupportError = React.useCallback(async () => {
    if (!supportErrorMessage) return;
    try {
      await navigator.clipboard.writeText(supportErrorMessage);
      setCopiedSupportError(true);
      setTimeout(() => setCopiedSupportError(false), 2000);
    } catch (error) {
      setCopiedSupportError(false);
    }
  }, [supportErrorMessage]);
  
  // Load the upload targets the caller may write to. This endpoint is tenancy-
  // scoped: a provider gets MainGroup + every company; a client gets just its own.
  // We always render the selector so it's clear where the rules will land.
  useEffect(() => {
    const fetchData = async () => {
      try {
        const targetsResponse = await axios.get('/api/upload-ids-rules');
        const d = targetsResponse.data || {};
        if (d.status) {
          const targets = d.available_target_tables || d.available_upload_targets || [];
          setAvailableTargets(targets);
          if (targets.length > 0) {
            setTargetCompany(targets[0]);   // default to the first available target
          }
        }
        clearImportIDSSupportError();
      } catch (err) {
        console.error('Error fetching initial data:', err);
        setImportIDSSupportError(
          FRONTEND_IMPORT_IDS_ERROR_CODES.INITIAL_LOAD_FAILED,
          'Import IDS Rules Page / Initial Data Load Failed',
          err?.message || String(err),
          true
        );
      }
    };

    fetchData();
  }, [clearImportIDSSupportError, setImportIDSSupportError]);
  
  const openFileModal = () => {
    setRuleFile({ value: null, error: '' });
    setShowModal(true);
  };
  
  const closeModal = () => {
    setShowModal(false);
    setRuleFile({ value: null, error: '' });
    setSuccess(prev => ({ ...prev, file: false }));
    setImportStats(null);
  };
  
  const handleAddSingleRule = async (e) => {
    e.preventDefault();
    
    // Validate input
    if (!singleRule.value.trim()) {
      setSingleRule({ ...singleRule, error: 'Rule cannot be empty' });
      return;
    }
    
    setLoading(true);
    
    try {
      const formData = new FormData();
      const ruleBlob = new Blob([singleRule.value], { type: 'text/plain' });
      formData.append('idsRulesFile', ruleBlob, 'rule.txt');
      formData.append('ruleEnabled', ruleEnabled ? '1' : '0');
      
      // Add target company (table name)
      // For FLC, targetCompany state is used. For others, it's derived or fixed.
      if (targetCompany) {
        formData.append('targetCompany', targetCompany);
      }
      
      const response = await axios.post('/api/upload-ids-rules', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.status) {
        setSuccess(prev => ({ ...prev, single: true }));
        clearImportIDSSupportError();
        
        // Clear the input after successful submission
        setTimeout(() => {
          setSingleRule({ value: '', error: '' });
          setSuccess(prev => ({ ...prev, single: false }));
        }, 3000);
      } else {
        const formattedError = buildImportIDSSupportError(
          FRONTEND_IMPORT_IDS_ERROR_CODES.SINGLE_RULE_ADD_FAILED,
          'Import IDS Rules Page / Add Single Rule Failed',
          response.data.msg || 'Failed to add rule'
        );
        setSingleRule({ 
          ...singleRule, 
          error: formattedError
        });
        setImportIDSSupportError(
          FRONTEND_IMPORT_IDS_ERROR_CODES.SINGLE_RULE_ADD_FAILED,
          'Import IDS Rules Page / Add Single Rule Failed',
          response.data.msg || 'Failed to add rule',
          false
        );
      }
    } catch (error) {
      console.error('Error adding IDS rule:', error);
      const rawError = error.response?.data?.msg || error.message || 'Failed to add rule';
      const formattedError = buildImportIDSSupportError(
        FRONTEND_IMPORT_IDS_ERROR_CODES.SINGLE_RULE_ADD_FAILED,
        'Import IDS Rules Page / Add Single Rule Request Error',
        rawError
      );
      setSingleRule({ 
        ...singleRule, 
        error: formattedError
      });
      setImportIDSSupportError(
        FRONTEND_IMPORT_IDS_ERROR_CODES.SINGLE_RULE_ADD_FAILED,
        'Import IDS Rules Page / Add Single Rule Request Error',
        rawError,
        false
      );
    } finally {
      setLoading(false);
    }
  };
  
  const handleImportRules = async (e) => {
    e.preventDefault();
    
    if (!ruleFile.value) {
      setRuleFile({ ...ruleFile, error: 'Please select a file to upload' });
      return;
    }
    
    setLoading(true);
    setUploadPhase('uploading');
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('idsRulesFile', ruleFile.value);

      if (targetCompany) {
        formData.append('targetCompany', targetCompany);
      }

      const response = await axios.post('/api/upload-ids-rules', formData, {
        onUploadProgress: (e) => {
          if (e.total) {
            const pct = Math.round((e.loaded / e.total) * 100);
            setUploadProgress(pct);
            // Bytes are on the wire; once they're all sent the server is parsing
            // and importing — switch to the indeterminate "processing" state.
            if (pct >= 100) setUploadPhase('processing');
          } else {
            setUploadPhase('processing');
          }
        },
      });

      if (response.data.status) {
        setSuccess(prev => ({ ...prev, file: true }));
        setRuleFile({ value: null, error: '' });
        clearImportIDSSupportError();
        // Capture statistics from response
        if (response.data.stats) {
          setImportStats(response.data.stats);
        }
      } else {
        const formattedError = buildImportIDSSupportError(
          FRONTEND_IMPORT_IDS_ERROR_CODES.FILE_IMPORT_FAILED,
          'Import IDS Rules Page / File Import Failed',
          response.data.msg || 'Failed to import rules'
        );
        setRuleFile({ 
          ...ruleFile, 
          error: formattedError
        });
        setImportIDSSupportError(
          FRONTEND_IMPORT_IDS_ERROR_CODES.FILE_IMPORT_FAILED,
          'Import IDS Rules Page / File Import Failed',
          response.data.msg || 'Failed to import rules',
          false
        );
      }
    } catch (error) {
      console.error('Error importing IDS rules:', error);
      const rawError = error.response?.data?.msg || error.message || 'Failed to import rules';
      const formattedError = buildImportIDSSupportError(
        FRONTEND_IMPORT_IDS_ERROR_CODES.FILE_IMPORT_FAILED,
        'Import IDS Rules Page / File Import Request Error',
        rawError
      );
      setRuleFile({ 
        ...ruleFile, 
        error: formattedError
      });
      setImportIDSSupportError(
        FRONTEND_IMPORT_IDS_ERROR_CODES.FILE_IMPORT_FAILED,
        'Import IDS Rules Page / File Import Request Error',
        rawError,
        false
      );
    } finally {
      setLoading(false);
      setUploadPhase('idle');
      setUploadProgress(0);
    }
  };
  
  if (error) {
    return (
      <>
        <Error />
        {supportErrorMessage && (
          <div className={styles.supportErrorCard}>
            <strong>Support Error:</strong>
            <code className={styles.supportErrorCode}>{supportErrorMessage}</code>
            <button
              type='button'
              className={styles.copySupportErrorButton}
              onClick={handleCopySupportError}
            >
              {copiedSupportError ? 'Copied' : 'Copy Error Details'}
            </button>
          </div>
        )}
      </>
    );
  }
  
  return (
    <Content headerTitle="Import IDS Rules" currentMenu="idsrules">
      {loading && <Loader />}
      
      <div className={styles.container}>
        <h2 className={styles.title}>Import IDS Rules</h2>
        {supportErrorMessage && (
          <div className={styles.supportErrorBanner}>
            <div className={styles.supportErrorBannerText}>
              <strong>Support Error:</strong>
              <code className={styles.supportErrorCode}>{supportErrorMessage}</code>
            </div>
            <button
              type='button'
              className={styles.copySupportErrorButton}
              onClick={handleCopySupportError}
            >
              {copiedSupportError ? 'Copied' : 'Copy Error Details'}
            </button>
          </div>
        )}
        
        {/* Where the rules land — MainGroup or a company for a provider, the
            client's own company otherwise. */}
        {availableTargets.length > 0 && (
          <div className={styles.companySelect}>
            <label htmlFor="targetCompany">Upload to:</label>
            <select
              id="targetCompany"
              value={targetCompany}
              onChange={(e) => setTargetCompany(e.target.value)}
              className={styles.select}
            >
              {availableTargets.map(table => (
                <option key={table} value={table}>
                  {table}
                </option>
              ))}
            </select>
          </div>
        )}
        
        <div className={styles.importOptionsContainer}>
          {/* Single Rule Input Section */}
          <div className={styles.importSection}>
            <h3>Add Single Rule</h3>
            <p>Add a single IDS rule to the database</p>
            
            <form onSubmit={handleAddSingleRule} className={styles.singleRuleForm}>
              <div className={styles.inputGroup}>
                <textarea 
                  className={styles.ruleTextarea}
                  placeholder="Enter IDS rule"
                  value={singleRule.value}
                  onChange={(e) => setSingleRule({ value: e.target.value, error: '' })}
                  rows={4}
                />
                {singleRule.error && <div className={styles.errorMessage}>{singleRule.error}</div>}
              </div>
              
              <div className={styles.ruleOptionsContainer}>
                <div className={styles.toggleContainer}>
                  <label className={styles.toggleLabel}>
                    Rule Status:
                    <div className={styles.statusText}>
                      {ruleEnabled ? 'Enabled' : 'Disabled'}
                    </div>
                    <div className={styles.toggle}>
                      <input
                        type="checkbox"
                        checked={ruleEnabled}
                        onChange={() => setRuleEnabled(!ruleEnabled)}
                        className={styles.toggleInput}
                      />
                      <span className={styles.toggleSlider}></span>
                    </div>
                  </label>
                </div>
              
                <button 
                  type="submit" 
                  className={styles.submitButton}
                  disabled={loading}
                >
                  Add Rule
                </button>
              </div>
              
              {success.single && (
                <div className={styles.successMessage}>
                  Rule added successfully!
                </div>
              )}
            </form>
          </div>
          
          {/* File Upload Section */}
          <div className={styles.importSection}>
            <h3>Import from File</h3>
            <p>Upload a .txt, .rules, or .csv file</p>
            
            <button 
              onClick={openFileModal} 
              className={styles.fileUploadButton}
              disabled={loading}
            >
              <div className={styles.buttonIcon}>
                <svg className="navigation__icon">
                  <use xlinkHref={`/assets/sprite.svg#icon-upload`} />
                </svg>
              </div>
              <span>Upload File</span>
            </button>
          </div>
        </div>
      </div>
      
      {/* Modal for importing from file */}
      <Modal show={showModal}>
        <ModalHeading heading="Import IDS Rules from File" />
        
        {success.file ? (
          <div className={styles.successMessage}>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '1rem' }}>
              IDS Rules imported successfully!
            </div>
            {importStats && (
              <>
                <div style={{ 
                  background: '#f8f9fa', 
                  padding: '1rem', 
                  borderRadius: '8px', 
                  marginBottom: '1rem',
                  textAlign: 'left'
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#333' }}>
                    Merge Statistics:
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0' }}>
                      <span style={{ color: '#666' }}>📝 Updated Rules:</span>
                      <span style={{ fontWeight: 'bold', color: '#0066cc' }}>{importStats.updated}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0' }}>
                      <span style={{ color: '#666' }}>➕ Added Rules:</span>
                      <span style={{ fontWeight: 'bold', color: '#28a745' }}>{importStats.added}</span>
                    </div>
                    <div style={{ 
                      borderTop: '2px solid #dee2e6', 
                      marginTop: '0.5rem', 
                      paddingTop: '0.5rem',
                      display: 'flex', 
                      justifyContent: 'space-between' 
                    }}>
                      <span style={{ fontWeight: 'bold', color: '#333' }}>Total Processed:</span>
                      <span style={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#333' }}>{importStats.total}</span>
                    </div>
                  </div>
                </div>
                
                {/* Warning box for skipped rules */}
                {importStats.skipped > 0 && (
                  <div style={{ 
                    background: '#fff3cd', 
                    border: '1px solid #ffc107',
                    padding: '0.75rem', 
                    borderRadius: '8px', 
                    marginBottom: '1rem',
                    textAlign: 'left'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                      <div>
                        <div style={{ fontWeight: 'bold', color: '#856404' }}>Warning:</div>
                        <div style={{ color: '#856404', fontSize: '0.9rem' }}>
                          {importStats.skipped} line{importStats.skipped > 1 ? 's' : ''} skipped due to missing SID numbers.
                          All rules must include a valid SID for proper management.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
              <button 
                onClick={closeModal}
                className={styles.submitButton}
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleImportRules}>
            <div className={styles.modalContent}>
              {/* Where the rules land (same targets as above). */}
              {availableTargets.length > 0 && (
                <div className={styles.modalInputGroup}>
                  <label htmlFor="modalTargetCompany">Upload to:</label>
                  <select
                    id="modalTargetCompany"
                    value={targetCompany}
                    onChange={(e) => setTargetCompany(e.target.value)}
                    className={styles.select}
                  >
                    {availableTargets.map(table => (
                      <option key={table} value={table}>
                        {table}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className={styles.modalInputGroup}>
                <label>Select Rules File:</label>
                <FileDropZone
                  accept=""
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setRuleFile({ value: e.target.files[0], error: '' });
                    }
                  }}
                  selectedFile={ruleFile.value}
                  errorMessage={ruleFile.error}
                  label="Drop IDS rules file here or click to browse"
                  helperText="Any text rules file (Suricata/Snort format), one rule per line"
                />
                <p className={styles.fileHelp}>
                  Files should contain compatible rules.
                </p>
              </div>

              {loading && (
                <div style={{ marginTop: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b', marginBottom: 6 }}>
                    <span>
                      {uploadPhase === 'processing'
                        ? 'Processing rules on the server…'
                        : 'Uploading…'}
                    </span>
                    {uploadPhase === 'uploading' && <span>{uploadProgress}%</span>}
                  </div>
                  <div style={{ height: 8, borderRadius: 4, background: '#e2e8f0', overflow: 'hidden' }}>
                    <div
                      style={{
                        height: '100%',
                        width: uploadPhase === 'processing' ? '100%' : `${uploadProgress}%`,
                        background: '#a20000',
                        borderRadius: 4,
                        transition: 'width 0.2s ease',
                        animation: uploadPhase === 'processing' ? 'idsPulse 1s ease-in-out infinite' : 'none',
                      }}
                    />
                  </div>
                  {uploadPhase === 'processing' && (
                    <p style={{ fontSize: 12, color: '#64748b', margin: '6px 0 0' }}>
                      A large ruleset can take a bit — please don&apos;t close this window.
                    </p>
                  )}
                  <style>{'@keyframes idsPulse{0%,100%{opacity:1}50%{opacity:.5}}'}</style>
                </div>
              )}
            </div>

            <div className={styles.modalButtonsContainer}>
              <button
                type="button"
                onClick={closeModal}
                className={styles.cancelButton}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={styles.importButton}
                disabled={loading}
              >
                {loading
                  ? (uploadPhase === 'processing' ? 'Processing…' : 'Uploading…')
                  : 'Import Rules'}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </Content>
  );
};

export default ImportIDSScreen;
